import { ref } from 'vue';

import { TOKEN_REFRESH_BUFFER_MS, AUTH_REFRESH_LOCK_APP_PREFIX } from '@/constants/auth';

import * as backendAuth from './backend-auth.service';
import { crossTabSync } from './crossTabSync';
import { expiresAtFromToken, parseScopes } from './jwtExpiry';
import { tokenStorage } from './tokenStorage';

const tokensMap = ref(new Map()); // bpId -> { accessToken, refreshToken, scopes, expiresAt }

function update(bpId, { accessToken, refreshToken, scopes }) {
  const id = String(bpId);
  const entry = {
    accessToken,
    refreshToken,
    scopes: parseScopes(scopes),
    expiresAt: expiresAtFromToken(accessToken),
  };
  // Reassign the Map (not just .set) so watchers driving CASL sync fire reliably.
  tokensMap.value = new Map(tokensMap.value).set(id, entry);
  tokenStorage.persistBPTokens(id, entry);
  crossTabSync.announceTokensUpdated(id); // cross-tab in-memory sync
  // Same-tab contract preserved from the old store: notifications.js reconnects the
  // WebSocket on this window event ({ businessProfileId, tokens: { access, refresh, scopes } }).
  window.dispatchEvent(
    new CustomEvent('tokensUpdated', {
      detail: {
        businessProfileId: id,
        tokens: { access: entry.accessToken, refresh: entry.refreshToken, scopes: entry.scopes },
      },
    }),
  );
  return entry;
}

export function hydrate() {
  tokensMap.value = tokenStorage.loadAllBPTokens();
}

// Fully delete one BP's token entry — in-memory AND storage. Storage-only removal is not enough:
// an entry already hydrated into tokensMap would keep serving (and refreshing) the stale token
// until the next reload.
export function remove(bpId) {
  const id = String(bpId);
  if (tokensMap.value.has(id)) {
    const next = new Map(tokensMap.value);
    next.delete(id);
    tokensMap.value = next;
  }
  tokenStorage.removeBPToken(id);
}

export function syncFromStorage(bpId) {
  const entry = tokenStorage.loadBPToken(bpId);
  if (entry) tokensMap.value = new Map(tokensMap.value).set(String(bpId), entry);
}

export function getFor(bpId) {
  return tokensMap.value.get(String(bpId))?.accessToken ?? null;
}

export function scopesFor(bpId) {
  return tokensMap.value.get(String(bpId))?.scopes ?? [];
}

export function isExpired(bpId) {
  const e = tokensMap.value.get(String(bpId));
  if (!e?.accessToken || !e?.expiresAt) return true;
  return Date.now() > e.expiresAt - TOKEN_REFRESH_BUFFER_MS;
}

// Whether we hold a refresh token for this BP that refresh() could rotate. When false, the caller
// must mint a new app token via fetchInitial() instead of refresh() (which would throw).
export function hasRefreshToken(bpId) {
  return !!tokensMap.value.get(String(bpId))?.refreshToken;
}

// Mint a per-BP app token by switching the backend session to that profile. Locked +
// existence-checked so concurrent callers/tabs don't double-mint.
export async function fetchInitial(bpId, { force = false } = {}) {
  return crossTabSync.withRefreshLock(`${AUTH_REFRESH_LOCK_APP_PREFIX}${bpId}`, async () => {
    syncFromStorage(bpId);
    // `force` re-mints even when a cached token still looks time-valid — used by the 401 handler,
    // where the backend rejected an otherwise-unexpired token. A background mint does NOT record
    // last-used (that's reserved for an explicit user switch via switchTo).
    if (!force && !isExpired(bpId)) return tokensMap.value.get(String(bpId)); // another caller minted it
    if (crossTabSync.signOutInProgress) return null; // don't mint a fresh token mid sign-out
    const data = await backendAuth.switchProfile(bpId);
    return update(bpId, data);
  });
}

// Force-mint a fresh per-BP app token via /auth/switch-profile, bypassing the cache. Used by an
// explicit user-initiated profile switch so the backend re-issues the scoped token AND records
// the selection as the user's last-used profile, even when a live token is already cached.
export async function switchTo(bpId) {
  return crossTabSync.withRefreshLock(`${AUTH_REFRESH_LOCK_APP_PREFIX}${bpId}`, async () => {
    if (crossTabSync.signOutInProgress) return null; // don't mint a fresh token mid sign-out
    // Explicit user switch: record the selection as the user's last-used profile.
    const data = await backendAuth.switchProfile(bpId, { recordLastUsed: true });
    return update(bpId, data);
  });
}

export async function refresh(bpId, { force = false } = {}) {
  return crossTabSync.withRefreshLock(`${AUTH_REFRESH_LOCK_APP_PREFIX}${bpId}`, async () => {
    syncFromStorage(bpId); // pick up another tab's rotation before deciding
    if (!force && !isExpired(bpId)) return tokensMap.value.get(String(bpId));
    if (crossTabSync.signOutInProgress) return null;
    const existing = tokensMap.value.get(String(bpId));
    if (!existing?.refreshToken) throw new Error(`No refresh token for BP ${bpId}`);
    try {
      const data = await backendAuth.refreshSession(existing.refreshToken, { forProfile: true });
      return update(bpId, data);
    } catch (err) {
      // Fallback path (no navigator.locks): another tab may have rotated the token first,
      // so our refreshToken is already stale → 4xx. Re-sync; if a newer valid token appeared,
      // use it instead of forcing a logout.
      syncFromStorage(bpId);
      const recovered = tokensMap.value.get(String(bpId));
      if (
        recovered?.accessToken &&
        recovered.accessToken !== existing.accessToken &&
        !isExpired(bpId)
      ) {
        return recovered;
      }
      throw err;
    }
  });
}

export function clearAll() {
  tokensMap.value = new Map();
  tokenStorage.clearAllBPTokens();
}

export const profileTokens = {
  tokensMap,
  getFor,
  scopesFor,
  isExpired,
  hasRefreshToken,
  fetchInitial,
  switchTo,
  refresh,
  remove,
  clearAll,
  hydrate,
  syncFromStorage,
};
